import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { useLenis } from "@/hooks/useLenis";
import { scrollToId } from "@/components/shared";
import { AuthProvider } from "@/context/AuthContext";
import { AuthDialog } from "@/components/AuthDialog";
import AuthCallback from "@/pages/AuthCallback";
import Shortlist from "@/pages/Shortlist";

import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { Difference } from "@/components/Difference";
import { Township } from "@/components/Township";
import { Inventory } from "@/components/Inventory";
import { AirportEffect } from "@/components/Airport";
import { AirportMobility } from "@/components/AirportMobility";
import { CorridorMap } from "@/components/CorridorMap";
import { AroundGYC } from "@/components/AroundGYC";
import { YeidaPlots } from "@/components/YeidaPlots";
import { FilmCity } from "@/components/FilmCity";
import { HiddenOpportunity } from "@/components/HiddenOpportunity";
import { Profiles } from "@/components/Profiles";
import { Projects } from "@/components/Projects";
import { CorridorExplorer } from "@/components/CorridorExplorer";
import { CompareSection } from "@/components/CompareSection";
import { Connectivity } from "@/components/Connectivity";
import { Timeline } from "@/components/Timeline";
import { Insights } from "@/components/Insights";
import { Advisory } from "@/components/Advisory";
import { LeadCapture, LeadDialog } from "@/components/LeadCapture";
import { SiteVisit } from "@/components/SiteVisit";
import { Trust } from "@/components/Trust";
import { FinalCTA } from "@/components/FinalCTA";
import { Footer } from "@/components/Footer";
import { Floating } from "@/components/Floating";
import Admin from "@/pages/Admin";
import Landing from "@/pages/Landing";
import { LANDING_LIST } from "@/data/landing";
import { SITE_URL } from "@/data/site";

const DIALOG_CONFIG = {
  advisor: { title: "Speak with a Radiance Advisor", context: "Share your brief - we respond with honest, corridor-level perspective." },
  inventory: { title: "Get Today's GYC Inventory", context: "Live apartments, plots, villas, commercial and verified resale - sent to you directly." },
  price: { title: "Request Price", context: null },
  yeida: { title: "YEIDA Scheme Alerts", context: "We will notify you when the next YEIDA residential plot scheme opens, with brochure, rates and draw dates." },
  "7pv": { title: "Enquire About 7th Park View", context: "Interested in a plot or villa in 7th Park View - please share your requirement." },
  yeidasector: { title: "YEIDA Sector Enquiry", context: null },
  ace: { title: "Enquire About This Ace Group Project", context: null },
  purvanchal: { title: "Enquire About This Project", context: null },
};
const LEAD_TYPE = {
  price: "request-price",
  yeida: "yeida-alert",
  "7pv": "7th-parkview-enquiry",
  yeidasector: "yeida-sector-enquiry",
  ace: "ace-yamuna-enquiry",
  purvanchal: "purvanchal-yamuna-enquiry",
};

function Home() {
  const [dialog, setDialog] = useState({ open: false, type: "advisor", context: null });

  useLenis();

  useEffect(() => {
    document.title = "Radiance Realtors | Gaur Yamuna City Property & Yamuna Growth Corridor Advisory";
    document.head.querySelector('link[rel="canonical"]')?.setAttribute("href", `${SITE_URL}/`);
    const id = window.location.hash.slice(1);
    if (id) setTimeout(() => scrollToId(id), 600);
  }, []);

  const openDialog = (type, context = null) => setDialog({ open: true, type, context });
  const cfg = DIALOG_CONFIG[dialog.type];

  return (
    <div className="grain">
      <Navbar onAdvisor={() => openDialog("advisor")} />
      <main>
        <Hero onAdvisor={() => openDialog("advisor")} onInventory={() => openDialog("inventory")} />
        <Marquee />
        <Difference />
        <Township />
        <Inventory onRequestPrice={(p) => openDialog("price", `Price request: ${p.project} (${p.config}, ${p.size})`)} />
        <AirportEffect />
        <AirportMobility />
        <CorridorMap />
        <AroundGYC />
        <YeidaPlots onAlert={() => openDialog("yeida")} />
        <FilmCity />
        <HiddenOpportunity />
        <Profiles onAdvisor={() => openDialog("advisor")} />
        <Projects />
        <CorridorExplorer
          onEnquire7pv={() => openDialog("7pv")}
          onEnquireYeidaSector={(ctx) => openDialog("yeidasector", ctx)}
          onEnquireAce={(ctx) => openDialog("ace", ctx)}
          onEnquirePurvanchal={(ctx) => openDialog("purvanchal", ctx)}
        />
        <CompareSection />
        <Connectivity />
        <Timeline />
        <Insights />
        <Advisory onAdvisor={() => openDialog("advisor")} />
        <LeadCapture onInventory={() => openDialog("inventory")} />
        <SiteVisit />
        <Trust />
        <FinalCTA onAdvisor={() => openDialog("advisor")} onInventory={() => openDialog("inventory")} />
      </main>
      <Footer />
      <Floating />
      <AuthDialog />
      <LeadDialog
        open={dialog.open}
        onClose={() => setDialog({ ...dialog, open: false })}
        leadType={LEAD_TYPE[dialog.type] || dialog.type}
        title={cfg.title}
        context={dialog.context || cfg.context}
      />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/shortlist" element={<Shortlist />} />
      {LANDING_LIST.map((p) => (
        <Route key={p.slug} path={p.path} element={<Landing slug={p.slug} />} />
      ))}
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <Toaster theme="dark" position="top-center" toastOptions={{ style: { background: "#141414", border: "1px solid rgba(212,175,55,0.3)", color: "#F5F5F0" } }} />
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
